import type { TranslationDictionary } from "./types";

export const en: TranslationDictionary = {
  // ── EmailGate ──
  "emailGate.exclusiveProposal": "Exclusive Proposal",
  "emailGate.yourProposalIsReady": "Your personalized proposal is ready",
  "emailGate.enterEmail": "Enter your email to access",
  "emailGate.placeholder": "your@email.com",
  "emailGate.invalidEmail": "Please enter a valid email",
  "emailGate.blockedEmail": "Please enter a valid corporate or personal email",
  "emailGate.accessProposal": "Access your exclusive proposal",
  "emailGate.loading": "Loading...",
  "emailGate.notFound": "Proposal not found",
  "emailGate.loadError":
    "Could not load the proposal. Please check the link or try again.",
  "emailGate.retry": "Try Again",
  "emailGate.socialProof": "+2,000 companies trust Woba",
  "emailGate.footer": "Powered by Woba",
  "emailGate.proposalLoadError": "Error loading proposal. Please try again.",

  // ── ProposalContent ──
  "proposal.notFoundOrExpired": "Proposal not found or expired.",

  // ── HeroSection ──
  "hero.preparedFor": "Prepared for {{companyName}}",
  "hero.titleWithCity": "Your office in {{city}}, ready to operate.",
  "hero.titleNoCity": "Your office, ready to operate.",
  "hero.optionSingular": "selected option",
  "hero.optionPlural": "selected options",
  "hero.allInclusive": "· All inclusive · One single contract.",
  "hero.spaceSingular": "{{count}} selected space",
  "hero.spacePlural": "{{count}} selected spaces",
  "hero.readyInCity": "Ready to operate in {{city}}",
  "hero.readyToOperate": "Ready to operate",
  "hero.singleContract": "One single contract",
  "hero.singleContractDesc": "For all company spaces",
  "hero.centralizedManagement": "Centralized management",
  "hero.centralizedManagementDesc": "No bureaucracy, no surprises",
  "hero.bestPrice": "Best price guaranteed",
  "hero.bestPriceDesc": "Price parity with direct contracting",
  "hero.startingFrom": "Starting from",
  "hero.perPositionMonth": "/position/month",
  "hero.viewSpaces": "View spaces",
  "hero.pricesGuaranteedUntil": "Prices guaranteed until {{date}}",
  "hero.contractTermLabel": "Contract term",
  "hero.contractTerm.6": "6 months",
  "hero.contractTerm.12": "12 months",
  "hero.contractTerm.24": "24 months",
  "hero.contractTerm.36": "36 months",

  // ── Chat ──
  "chat.contextLabel": "Asking about",
  "chat.placeholder": "Ask about this space...",
  "chat.welcomeLine1":
    "Hello! 👋 I'm here to answer your questions about **{{spaceName}}**.",
  "chat.welcomeDetailsPrefix": "This space is located",
  "chat.welcomeAt": "at {{address}}",
  "chat.welcomeCapacity": "with capacity for **{{capacity}} positions**",
  "chat.welcomePrice": "at **R$ {{price}}/position/month**",
  "chat.welcomeAskAnything": "Ask anything — or click the suggestions below!",
  "chat.errorMessage":
    "Sorry, an error occurred while processing your message. Please try again.",
  "chat.dividerNowAbout": "Now about: {{spaceName}}",
  "chat.sendAriaLabel": "Send",
  "chat.chip.monthlyValue": "What is the monthly cost?",
  "chat.chip.parking": "Is there parking?",
  "chat.chip.internet": "What is the internet speed?",
  "chat.chip.meetingRooms": "Are meeting rooms included?",
  "chat.chip.credits": "How do Woba credits work?",
  "chat.chip.contract": "What is the contract flexibility?",
  "chat.chip.scheduleVisit": "I want to schedule a visit",

  // ── SpacesSection ──
  "spaces.selectedSpaces": "Selected Spaces",
  "spaces.optionsFor": "Options for {{companyName}}",
  "spaces.optionsForDefault": "Options for your company",
  "spaces.bestOfficesInCity":
    "We selected the best offices in {{city}} for your operation",
  "spaces.bestOptions": "We selected the best options for your operation",
  "spaces.availableOptions": "Available Options",
  "spaces.recommended": "★ Recommended",
  "spaces.combo": "Set",
  "spaces.pos": "pos",
  "spaces.tabPhotos": "Photos",
  "spaces.tabMap": "Map",
  "spaces.tabAmenities": "Amenities",
  "spaces.tabChat": "Questions",
  "spaces.scopeOffice": "Private Office",
  "spaces.scopeSpace": "Space",
  "spaces.tabNavLabel": "Space navigation",
  "spaces.fullscreenTitle": "Spaces Location",

  // ── PhotosTab ──
  "photos.noImage": "No image available",
  "photos.didYouLike": "Did you like it?",
  "photos.liked": "Liked",
  "photos.disliked": "Disliked",
  "photos.positions": "positions",
  "photos.perMonth": "/month",
  "photos.costRent": "Rent + Condo fees + Property tax 53%",
  "photos.costServices": "Services 32%",
  "photos.costSupport": "Support 15%",
  "photos.perPosition": "R$ {{price}} per position",
  "photos.priceOnRequest": "Price on request",
  "photos.licenseFee": "License Fee R$ {{price}}",
  "photos.licenseFeeIncluded": "License Fee included",

  // ── AmenitiesTab ──
  "amenities.notAvailable": "Amenity information not available.",

  // ── MapTab ──
  "map.coworkings": "Coworkings",
  "map.fullscreen": "Fullscreen",
  "map.fullscreenAriaLabel": "View map in fullscreen",
  "map.calculateDistance": "Calculate distance from...",
  "map.placeholder": "e.g., Av. Paulista, 1000",
  "map.searching": "Searching...",
  "map.calculate": "Calculate",
  "map.distancesFrom": "Distances from",
  "map.addressNotProvided": "Address not provided",
  "map.selectedNoAddress": "Address not provided for this option",
  "map.recenter": "Recenter",
  "map.recenterTitle": "Recenter map",
  "poi.noDescription": "No description",

  // ── ComparisonSection ──
  "comparison.heading": "The smartest way to hire and manage offices.",
  "comparison.subheadingPre":
    "Leave behind the complexities of the traditional model and",
  "comparison.subheadingBold": "centralize everything in one smart contract",
  "comparison.subheadingPost": "— switch to the Woba model and discover.",
  "comparison.tabTraditional": "Traditional lease",
  "comparison.tabWoba": "Woba",
  "comparison.traditional.title": "The costs you forget to add up.",
  "comparison.traditional.desc":
    "Because in the traditional model, rent is just the beginning.",
  "comparison.woba.title": "One single price, no surprises",
  "comparison.woba.desc":
    "With the Woba model, you have one single price, no surprises — from rent to cleaning.",

  // ── SpaceComparisonTable ──
  "table.badge": "Comparison",
  "table.heading": "Space Comparison",
  "table.subheading": "All options side by side to ease your decision",
  "table.colSpace": "Space",
  "table.colLocation": "Location",
  "table.colPositions": "Positions",
  "table.colArea": "Area",
  "table.colPricePerPosition": "R$/Position",
  "table.colCreditsPerPosition": "Credits/Position",
  "table.colMonthly": "Monthly fee",
  "table.colCreditsMonth": "Credits/Month",
  "table.colLicenseFee": "License Fee",
  "table.licenseFeeTooltip": "One-time payment",
  "table.colVisit": "Visit",
  "table.included": "Included",
  "table.belowMarket": "▼ {{pct}}% below market",
  "table.wantToVisit": "I want to visit",
  "table.selected": "Selected",
  "table.sent": "Sent",
  "table.bestPriceGuarantee": "Best price guarantee",
  "table.bestPriceDesc": "— Same price as direct contracting.",
  "table.marketSource":
    "Compared with the region's market average. Source: Woba database.",
  "table.credits": "Credits",
  "table.visitBarSingular": "space selected for visit",
  "table.visitBarPlural": "spaces selected for visit",
  "table.scheduleVisits": "Schedule visits",

  // ── BeforeAfterSection ──
  "beforeAfter.badge": "Before vs After",
  "beforeAfter.heading": "Traditional model vs. Woba",
  "beforeAfter.subheading": "Fewer steps, lower cost, more control.",
  "beforeAfter.traditional": "Traditional",
  "beforeAfter.withWoba": "With Woba",
  "beforeAfter.trad.suppliers": "13",
  "beforeAfter.trad.suppliersDesc": "different suppliers",
  "beforeAfter.trad.timeline": "3–6",
  "beforeAfter.trad.timelineDesc": "to implement the operation",
  "beforeAfter.trad.timelineSuffix": "months",
  "beforeAfter.trad.variableCosts": "Variable costs",
  "beforeAfter.trad.variableCostsDesc": "different surprises every month",
  "beforeAfter.trad.closedContract": "Locked contract",
  "beforeAfter.trad.closedContractDesc":
    "3 to 6 months penalty for cancellation",
  "beforeAfter.trad.noVisibility": "No visibility",
  "beforeAfter.trad.noVisibilityDesc": "data spread across multiple suppliers",
  "beforeAfter.trad.blindDecision": "Blind decision",
  "beforeAfter.trad.blindDecisionDesc": "no data to optimize costs",
  "beforeAfter.woba.singleContract": "1",
  "beforeAfter.woba.singleContractDesc": "single contract, all inclusive",
  "beforeAfter.woba.timeline": "15",
  "beforeAfter.woba.timelineDesc": "operating with full onboarding",
  "beforeAfter.woba.timelineSuffix": "days",
  "beforeAfter.woba.fixedValue": "Fixed monthly value",
  "beforeAfter.woba.fixedValueDesc": "no surprises, no hidden costs",
  "beforeAfter.woba.onDemand": "Build on demand",
  "beforeAfter.woba.onDemandDesc": "expand, reduce or switch without penalty",
  "beforeAfter.woba.dashboard": "Real-time dashboard",
  "beforeAfter.woba.dashboardDesc": "occupancy, costs and usage in one place",
  "beforeAfter.woba.ai": "AI that optimizes operations",
  "beforeAfter.woba.aiDesc": "identifies waste and suggests adjustments",
  "beforeAfter.woba.bestPrice": "Best price guaranteed",
  "beforeAfter.woba.bestPriceDesc":
    "same price as direct contracting — with the full platform included",
  "beforeAfter.accordionLabel": "View breakdown of the 13 costs you eliminate",
  "beforeAfter.accordionIntro": "What you would need to manage separately:",
  "beforeAfter.accordionCat1": "Rent + Condo fees + Property tax (48-58%)",
  "beforeAfter.accordionCat1.rent": "Base rent",
  "beforeAfter.accordionCat1.condo": "Condo fees",
  "beforeAfter.accordionCat1.tax": "Property tax",
  "beforeAfter.accordionCat2": "Operational services (28-35%)",
  "beforeAfter.accordionCat2.energy": "Energy",
  "beforeAfter.accordionCat2.staff": "Operational staff",
  "beforeAfter.accordionCat2.cleaning": "Cleaning",
  "beforeAfter.accordionCat2.internet": "Internet / Telecom",
  "beforeAfter.accordionCat2.maintenance": "Maintenance",
  "beforeAfter.accordionCat2.water": "Water",
  "beforeAfter.accordionCat3": "Support and depreciation (12-18%)",
  "beforeAfter.accordionCat3.furniture": "Furniture (depreciation)",
  "beforeAfter.accordionCat3.coffee": "Coffee / Amenities",
  "beforeAfter.accordionCat3.insurance": "Insurance",
  "beforeAfter.accordionCat3.admin": "Admin / Overhead",
  "beforeAfter.accordionSummaryBold1": "13 items",
  "beforeAfter.accordionSummaryMiddle":
    "to manage, 13 suppliers, 13 contracts.",
  "beforeAfter.accordionSummaryBold2": "With Woba, all in 1.",

  // ── NetworkSection ──
  "network.badge": "Woba Network",
  "network.heading": "You hire an office.\nYou get an entire country.",
  "network.subheading":
    "When you contract through Woba, your team is not tied to one address. With the credits included in the plan, they can use meeting rooms, workstations and event spaces in any city in Brazil.",
  "network.stat.flexibleOffices": "flexible offices",
  "network.stat.meetingRooms": "meeting rooms",
  "network.stat.eventSpaces": "event spaces",
  "network.stat.cities": "cities in Latin America",
  "network.type.workstations": "Workstations",
  "network.type.workstationsDescPre": "1 day pass at any of the",
  "network.type.workstationsDescCount": "+23,000",
  "network.type.workstationsDescPost": "workstations",
  "network.type.meetingRooms": "Meeting Rooms",
  "network.type.meetingRoomsDescPre": "1 hour at any of the",
  "network.type.meetingRoomsDescCount": "+4,200",
  "network.type.meetingRoomsDescPost": "rooms",
  "network.type.eventSpaces": "Event Spaces",
  "network.type.eventSpacesDescPre": "1 day pass at any of the",
  "network.type.eventSpacesDescCount": "+1,000",
  "network.type.eventSpacesDescPost": "spaces",
  "network.type.privateOffices": "Private Offices",
  "network.type.privateOfficesDescPre": "1 month at any of the",
  "network.type.privateOfficesDescCount": "+5,200",
  "network.type.privateOfficesDescPost": "offices",
  "network.credits": "credits",
  "network.creditsPerPerson": "credits/person",
  "network.creditsPerPosition": "credits/position",

  // ── FlexibilitySection ──
  "flexibility.title": "Your operation changes. Your contract keeps up.",
  "flexibility.desc":
    "Need more positions? We expand. Need to downsize? Renegotiate without abusive penalties. Want to change address? We handle the migration.",

  // ── NextStepsSection ──
  "nextSteps.heading": "Next Steps",
  "nextSteps.subheading": "From interest to operation in just a few days",
  "nextSteps.step1.title": "Schedule a visit",
  "nextSteps.step1.desc": "See the space in person",
  "nextSteps.step1.timing": "Today",
  "nextSteps.step2.title": "Final proposal",
  "nextSteps.step2.desc": "Pricing, conditions and SLA",
  "nextSteps.step2.timing": "48h after visit",
  "nextSteps.step3.title": "Sign and occupy",
  "nextSteps.step3.desc": "Digital contract, move-in coordinated by Woba",
  "nextSteps.step3.timing": "Up to 15 days",
  "nextSteps.scheduleVisits": "Schedule visits",
  "nextSteps.seeSpaces": "See the selected spaces and schedule your visit.",
  "nextSteps.consultantQuestion":
    "Still have questions? Talk to our specialist",
  "nextSteps.consultantRole": "Woba Consultant",

  // ── CTASection / Footer ──
  "cta.tagline": "The smartest way to hire and manage offices",
  "cta.contact": "Contact",
  "cta.proposal": "Proposal",
  "cta.generatedOn": "Generated on:",
  "cta.validUntil": "Valid until:",
  "cta.validTBD": "To be defined",
  "cta.copyright": "© {{year}} Woba. All rights reserved.",
  "cta.pricesGuaranteedUntil": "Prices guaranteed until",
  "cta.afterDate": "After this date, prices adjusted based on availability",
  "cta.scheduleVisits": "Schedule visits",
  "cta.chooseSpaces": "Choose spaces to visit",
  "cta.scheduleVisitsCount": "Schedule visit at {{count}} location",
  "cta.scheduleVisitsCountPlural": "Schedule visits at {{count}} locations",

  // ── LogosSection ──
  "logos.socialProof": "Some of the thousands of companies that trust Woba",

  // ── FlexCreditsSection ──
  "flexCredits.label": "Extra Flex Credits",
  "flexCredits.perMonth": "credits/month",
  "flexCredits.learnMore": "Learn more",

  // ── ScheduleModal ──
  "schedule.title": "Schedule Visits",
  "schedule.subtitle": "Choose the date and time for each space",
  "schedule.availableUntil": "Scheduling available until",
  "schedule.chooseDate": "Choose the date",
  "schedule.chooseTime": "Choose the time",
  "schedule.confirm": "Confirm scheduling",
  "schedule.sending": "Sending...",
  "schedule.sentLabel": "Scheduling sent —",
  "schedule.at": "at",
  "schedule.successTitle": "Request sent!",
  "schedule.successSubtitle": "Your visits have been requested",
  "schedule.successNotice":
    "Times still depend on space confirmation. We will contact you soon.",
  "schedule.understood": "Got it",

  // ── Spaces (additional) ──
  "spaces.option": "Option {{number}}",
  "spaces.ofTotal": "of {{total}}",

  // ── POI Categories ──
  "poi.restaurant": "Restaurants",
  "poi.cafe": "Cafés",
  "poi.pharmacy": "Pharmacies",
  "poi.transport": "Transport",
  "poi.hotel": "Hotels",
  "poi.parking": "Parking",

  // ── Chat (additional) ──
  "chat.defaultSpaceName": "this space",

  // ── FlexCredits (additional) ──
  "flexCredits.estimatedValue": "R$ {{value}}/mo",

  // ── LicenseFee ──
  "licenseFee.title": "License Fee",
  "licenseFee.paidSemiannually": "paid semiannually",
  "licenseFee.paidAnnually": "paid annually",
  "setupFee.title": "Setup Fee",
  "setupFee.paidOnce": "one-time payment",
};
